/**
 * Analysis report viewer (SA-212) — /admin/conversation-analysis/runs/:runId.
 * Polls while the run is `running` (the backend presents stale running rows
 * as `error`, so polling always terminates); renders the structured report
 * when it matches the known shape, else the server's deterministic
 * report_markdown via DocMarkdown, else an empty state.
 */
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Coins, Cpu, Download, Filter, MessagesSquare, Printer } from 'lucide-react'

import { DocMarkdown } from '@/components/doc/DocMarkdown'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button, Spinner, StatCard } from '@/components/ui'
import { useCanAccessFeature, useFeature } from '@/hooks'
import { useAnalysisRun } from '@/hooks/useConversationAnalysis'
import { apiClient, ApiError } from '@/services/apiClient'
import { DeepDiveSections, reportHasDeepDiveShape } from './DeepDiveSections'
import { ReportSections, reportHasCoreShape } from './ReportSections'
import { RunStatusChip } from './RunStatusChip'
import {
  analysisErrorMessage, formatCost, formatFilterSummary, reportTypeLabel,
} from './runFormat'

/** The title reads as the old `capitalize` CSS drew it, now that it is the tab's title too. */
const capitalizeWords = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase())

export function AnalysisRunPage() {
  const canAccess = useCanAccessFeature('conversation_analytics', 'conversation_analysis:read')
  const { runId } = useParams<{ runId: string }>()
  const run = useAnalysisRun(runId, canAccess)
  const inboxEnabled = useFeature('inbox')
  const [exportError, setExportError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  async function downloadMarkdown() {
    if (!runId) return
    setExportError(null)
    setDownloading(true)
    try {
      // Auth-gated blob download — a bare <a href> can't carry the
      // in-memory Bearer token (ConversationsExportControl precedent).
      const res = await apiClient.fetch(`/api/conversation-analysis/runs/${runId}/export`)
      if (!res.ok) {
        let detail = `Export failed (${res.status})`
        try {
          const body = await res.json()
          if (typeof body?.detail === 'string') detail = body.detail
        } catch { /* keep generic message */ }
        setExportError(detail)
        return
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `conversation-analysis-${run.data?.reportType ?? 'report'}-${runId.slice(0, 8)}.md`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      setExportError('Export failed — check your connection and try again')
    } finally {
      setDownloading(false)
    }
  }

  if (!canAccess) {
    return (
      <PageFrame width="form" className="py-8">
        <p className="text-sm text-muted-foreground">
          You don't have access to Conversation Analysis.
        </p>
      </PageFrame>
    )
  }

  return (
    <PageFrame width="detail" className="space-y-4">
      <PageHeader
        title={capitalizeWords(run.data ? `${reportTypeLabel(run.data.reportType)} report` : 'Report')}
        sticky
        back={{ to: '/admin/conversation-analysis', label: 'Conversation Analysis' }}
        tag={run.data && <RunStatusChip status={run.data.status} />}
        subtitle={run.data && formatFilterSummary(run.data.filters)}
        actions={
          run.data?.status === 'completed' && (
            <div className="flex items-center gap-2 print:hidden">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={downloading}
                onClick={downloadMarkdown}
              >
                <Download className="w-4 h-4" />
                {downloading ? 'Downloading…' : 'Download .md'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => window.print()}
              >
                <Printer className="w-4 h-4" />
                Print / Save as PDF
              </Button>
            </div>
          )
        }
      />

      {exportError && (
        <p role="alert" className="text-sm text-destructive print:hidden">
          {exportError}
        </p>
      )}

      {run.isLoading && (
        <p className="py-8 text-sm text-muted-foreground">Loading run…</p>
      )}
      {run.isError && (
        <p role="alert" className="py-8 text-sm text-destructive">
          {run.error instanceof ApiError && run.error.status === 404
            ? 'Run not found — it may have been deleted.'
            : `Couldn't load the run — ${analysisErrorMessage(run.error)}`}
        </p>
      )}

      {run.isSuccess && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={MessagesSquare}
              label="Analyzed"
              value={`${run.data.analyzedCount} / ${run.data.conversationCount}`}
            />
            <StatCard icon={Filter} label="Skipped" value={String(run.data.skippedCount)} />
            {/* Cost/model are hidden in print (SA-214 final-review ruling):
                the printed PDF leaves the admin login same as the .md
                export, which deliberately omits cost/tokens/provider/model. */}
            <StatCard
              icon={Coins}
              label="Cost"
              value={formatCost(run.data.totalCostUsd)}
              className="print:hidden"
            />
            <StatCard
              icon={Cpu}
              label="Model"
              value={run.data.model ?? '—'}
              sublabel={run.data.provider ?? undefined}
              className="print:hidden"
            />
          </div>

          {run.data.status === 'running' && (
            <div
              role="status"
              className="rounded-sm border border-info/30 bg-info/10 px-3 py-2 text-xs text-info flex items-center gap-2"
            >
              <Spinner size="sm" aria-hidden="true" />
              Analyzing conversations — this can take a few minutes. The page
              updates automatically.
            </div>
          )}
          {run.data.status === 'error' && (
            <p
              role="alert"
              className="rounded-sm border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {run.data.error ?? 'The run failed.'}
            </p>
          )}
          {run.data.status === 'completed' &&
            (() => {
              const fallback = run.data.reportMarkdown ? (
                // Report schema evolved ahead of this renderer — show the
                // server's deterministic markdown instead.
                <DocMarkdown content={run.data.reportMarkdown} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  This run produced no report.
                </p>
              )
              if (run.data.reportType === 'deep_dive') {
                return reportHasDeepDiveShape(run.data.report) ? (
                  <DeepDiveSections run={run.data} inboxEnabled={inboxEnabled} />
                ) : (
                  fallback
                )
              }
              return reportHasCoreShape(run.data.report) ? (
                <ReportSections run={run.data} inboxEnabled={inboxEnabled} />
              ) : (
                fallback
              )
            })()}
        </>
      )}
    </PageFrame>
  )
}
